/**
 * Checklist Item Drawer Component
 * 
 * Drawer for adding/editing checklist questions (Admin only)
 * Features:
 * - Simple text area for question
 * - Add mode: creates new question
 * - Edit mode: updates existing question (keeping other fields like response)
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../../../components/common/ButtonGroup';
import type { ChecklistItem } from '../../../../../services/certificationService';

const { TextArea } = Input;

// ========== TYPE DEFINITIONS ==========

export interface ChecklistItemFormData {
  checklistLineItemId?: string; // Only for edit mode (existing items from API)
  question: string;
  // Keep original data for edit mode (to not override response, etc.)
  originalItem?: ChecklistItem;
}

export interface ChecklistItemDrawerProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: ChecklistItem | null;
  insertAtIndex?: number | null; // For "add above" / "add below" functionality
  onClose: () => void;
  onSubmit: (values: ChecklistItemFormData) => void;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledTextArea = styled(TextArea)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #1F2937;
  min-height: 150px !important;

  &.ant-input {
    color: #1F2937;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

// ========== MAIN COMPONENT ==========

const ChecklistItemDrawer: React.FC<ChecklistItemDrawerProps> = ({
  open,
  mode,
  initialValues,
  insertAtIndex,
  onClose,
  onSubmit,
}) => {
  // Form states
  const [form] = Form.useForm();
  const [question, setQuestion] = useState('');

  // UI states
  const [isFormValid, setIsFormValid] = useState(false);

  // Reset form when drawer opens/closes or mode changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialValues) {
        // Edit mode - load existing question
        setQuestion(initialValues.question || '');
        form.setFieldsValue({
          question: initialValues.question,
        });
      } else {
        // Add mode - reset to empty
        resetForm();
      }
    }
  }, [open, mode, initialValues, form]);

  // Watch for field changes and validate
  useEffect(() => {
    const hasQuestion = !!question && question.trim() !== '';
    setIsFormValid(hasQuestion);
  }, [question]);

  const resetForm = () => {
    setQuestion('');
    form.resetFields();
  };

  const handleClose = () => {
    setIsFormValid(false);
    resetForm();
    onClose();
  };

  const handleFormSubmit = () => {
    const formData: ChecklistItemFormData = {
      question: question.trim(),
    };

    // Include checklistLineItemId for edit mode (existing items from API)
    if (mode === 'edit' && initialValues) {
      // Only include checklistLineItemId if it's a real ID (not a draft ID)
      if (initialValues.checklistLineItemId && !initialValues.checklistLineItemId.startsWith('draft-')) {
        formData.checklistLineItemId = initialValues.checklistLineItemId;
      }
      // Keep the original item data for reference
      formData.originalItem = initialValues;
    }

    onSubmit(formData);
    handleClose();
  };

  const getDrawerTitle = () => {
    if (mode === 'edit') {
      return 'Edit Question';
    }
    if (insertAtIndex !== null && insertAtIndex !== undefined) {
      return 'Add Question';
    }
    return 'Add Question';
  };

  return (
    <RightContentDrawer
      open={open}
      title={getDrawerTitle()}
      width={572}
      onClose={handleClose}
      footer={
        <ButtonGroup>
          <Button
            type="default"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleFormSubmit}
            disabled={!isFormValid}
          >
            {mode === 'add' ? 'Add Question' : 'Save Changes'}
          </Button>
        </ButtonGroup>
      }
    >
      <StyledForm
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        {/* Question */}
        <Form.Item
          name="question"
          rules={[
            { required: true, message: 'Please enter the question' },
            { whitespace: true, message: 'Question cannot be empty' },
          ]}
        >
          <div>
            <FormLabel>Question</FormLabel>
            <StyledTextArea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter the change indicator question..."
              rows={6}
              spellCheck={true}
              autoFocus
            />
            <InfoText>
              Enter a question that the Process Owner will need to answer during certification.
            </InfoText>
          </div>
        </Form.Item>
      </StyledForm>
    </RightContentDrawer>
  );
};

export default ChecklistItemDrawer;

